/* eslint-disable @typescript-eslint/naming-convention */
import { useI18n } from '@udemy/i18n';
import { Icon } from '@udemy/icons';
import { Button } from '@udemy/react-core-components';
import classNames from 'classnames';
import React, { useState } from 'react';
import styles from "./alert-banner.module.css";

/** The AlertBanner component.  A wrapper for the {@link AlertBannerContent} component, with some event handlers. */
export const AlertBanner = props => {
  const [dismissed, setDismissed] = useState(false);

  function handleAction() {
    var _props$onAction;

    setDismissed(true);
    (_props$onAction = props.onAction) === null || _props$onAction === void 0 ? void 0 : _props$onAction.call(props);
  }

  function handleDismiss() {
    var _props$onDismiss;

    setDismissed(true);
    (_props$onDismiss = props.onDismiss) === null || _props$onDismiss === void 0 ? void 0 : _props$onDismiss.call(props);
  }

  if (dismissed) {
    return null;
  } // eslint-disable-next-line react/jsx-no-bind


  return /*#__PURE__*/React.createElement(AlertBannerContent, Object.assign({}, props, {
    onAction: handleAction,
    onDismiss: handleDismiss
  }));
};

/** The AlertBannerContent component is the internal scaffolding of an {@link AlertBanner} component.
 *  This is made to be used within an `AlertBanner`, but can be used in other custom containers as well.
 */
export const AlertBannerContent = _ref => {
  let {
    actionButtonProps = {},
    body,
    className,
    dismissButtonProps = {},
    icon,
    onAction,
    onDismiss,
    showCta = true,
    showIcon = true,
    title,
    udStyle = 'information',
    backgroundStyle = 'bordered',
    ...props
  } = _ref;
  const {
    gettext
  } = useI18n();
  const {
    ctaText = gettext('Take action'),
    dismissButtonText = gettext('Dismiss'),
    ...divProps
  } = props;
  const screenReaderLabels = {
    information: gettext('information alert'),
    success: gettext('success alert'),
    error: gettext('error alert'),
    warning: gettext('warning alert')
  };
  const textFrameIconClass = showIcon ? styles['text-frame-with-icon'] : '';
  return /*#__PURE__*/React.createElement("div", Object.assign({}, divProps, {
    className: classNames(styles['alert-banner'], styles[backgroundStyle], styles[`alert-banner-${udStyle}`], className)
  }), showIcon && (icon || alertBannerContentIcon(udStyle)), /*#__PURE__*/React.createElement("div", {
    style: {
      flex: 1
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['text-frame'], textFrameIconClass)
  }, /*#__PURE__*/React.createElement("span", {
    className: "ud-sr-only"
  }, screenReaderLabels[udStyle]), title && /*#__PURE__*/React.createElement("h2", {
    className: "ud-heading-md"
  }, title), body && /*#__PURE__*/React.createElement("div", {
    className: classNames('ud-text-sm', styles.body)
  }, body)), showCta && /*#__PURE__*/React.createElement("div", {
    className: styles['cta-container']
  }, actionButtonProps && /*#__PURE__*/React.createElement(Button, Object.assign({
    "data-purpose": "action",
    onClick: onAction,
    udStyle: "secondary",
    size: "medium"
  }, actionButtonProps, {
    className: styles['action-btn']
  }), ctaText), dismissButtonProps && /*#__PURE__*/React.createElement(Button, Object.assign({
    "data-purpose": "dismiss",
    onClick: onDismiss,
    size: "medium",
    udStyle: "ghost",
    className: styles['dismiss-btn']
  }, dismissButtonProps), dismissButtonText))));
};
/**
 * Determines which icon to display based on the `AlertLevel` type.
 * @param udStyle The current {@link AlertLevel} of the banner.
 * @returns A `Icon` component
 */

function alertBannerContentIcon(udStyle) {
  let color = 'info';
  let glyph = 'info-outline';

  switch (udStyle) {
    case 'warning':
      color = 'warning';
      glyph = 'warning';
      break;

    case 'success':
      color = 'positive';
      glyph = 'success-outline';
      break;

    case 'error':
      color = 'negative';
      glyph = 'error';
      break;
  }

  return /*#__PURE__*/React.createElement(Icon, {
    color: color,
    glyph: glyph,
    label: false,
    size: "large"
  });
}
//# sourceMappingURL=alert-banner.react-component.js.map